import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { CatalogAuthorsComponent } from '@app/pages/admin/developer-catalog-page/catalog-authors/catalog-authors';
import { DeveloperCatalogStore } from '@app/pages/admin/developer-catalog-page/developer-catalog.store';
import {
  CatalogCommand,
  catalogGroupLabel,
  catalogKindSeverity,
  commandReferenceCount,
  commandReferenceSources,
  cqrsIssuesTooltip,
  developerCatalogCommandLink,
  developerCatalogJobLink,
  isCatalogItemObsolete,
  isCqrsCompliant,
  isPlacementCompliant,
  placementIssuesTooltip,
} from '@app/pages/admin/developer-catalog-page/developer-catalog.utils';
import { combineLatest, map, switchMap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';

@Component({
  selector: 'app-developer-catalog-command-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    TagModule,
    TooltipModule,
    BackButton,
    Loading,
    CatalogAuthorsComponent,
    TranslateModule,
    ServerTranslatePipe,
    CopyableIdentifierComponent,
  ],
  templateUrl: './developer-catalog-command-page.html',
  styleUrl: './developer-catalog-command-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeveloperCatalogCommandPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(DeveloperCatalogStore);

  readonly commandLink = developerCatalogCommandLink;
  readonly jobLink = developerCatalogJobLink;
  readonly commandReferenceCount = commandReferenceCount;
  readonly commandReferenceSources = commandReferenceSources;
  readonly catalogKindSeverity = catalogKindSeverity;
  readonly isCqrsCompliant = isCqrsCompliant;
  readonly cqrsIssuesTooltip = cqrsIssuesTooltip;
  readonly isPlacementCompliant = isPlacementCompliant;
  readonly placementIssuesTooltip = placementIssuesTooltip;
  readonly isCatalogItemObsolete = isCatalogItemObsolete;
  readonly catalogGroupLabel = catalogGroupLabel;

  private readonly state = toSignal(
    this.route.paramMap.pipe(
      map((params) => params.get('name') ?? ''),
      switchMap((name) =>
        combineLatest([this.store.loading$, this.store.findCommand(name)]).pipe(
          map(([loading, command]) => ({ loading, command, name })),
        ),
      ),
    ),
    { initialValue: { loading: true, command: null as CatalogCommand | null, name: '' } },
  );

  loading = computed(() => this.state().loading);
  error = toSignal(this.store.error$, { initialValue: null as string | null });
  command = computed(() => this.state().command);
  notFound = computed(() => !this.state().loading && !this.state().command);
  title = computed(() => this.command()?.name || this.state().name || 'Command');

  formatJson(value?: string | null): string {
    if (!value) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
}
